import { useGetStations } from "@/entities/station";
import { Rijtijd, useGetTvvRijtijd } from "@/entities/tvv";
import { getStationOptions } from "@/features/infra/components/utils";
import { TimelineLine, TimelineStationIndicator } from "@/features/tvv/components/shared/route-timeline";
import { ApiError } from "@/infrastructure/rest";
import { Button, Icon } from "@/shared/components/ui";
import { cn } from "@/shared/utils";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragStartEvent,
    KeyboardSensor,
    Modifier,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StationRow } from "./station-row";

const restrictToVerticalAxis: Modifier = ({ transform }) => ({
    ...transform,
    x: 0,
});

export type EditableStation = {
    id: string;
    code: string;
};

type EditableStationsListProps = {
    stations: EditableStation[];
    rijtijden: Rijtijd[];
    onStationsChange: (stations: EditableStation[]) => void;
    onRijtijdChange: (beginStation: string, eindStation: string, reisMinuten: number | undefined) => void;
    onHasLoadingRijtijden: (hasLoading: boolean) => void;
    onHasInvalidRijtijden: (hasInvalid: boolean) => void;
};

export function EditableStationsList({
    stations,
    rijtijden,
    onStationsChange,
    onRijtijdChange,
    onHasLoadingRijtijden,
    onHasInvalidRijtijden,
}: Readonly<EditableStationsListProps>) {
    const { data: allStations } = useGetStations();
    const stationOptions = useMemo(() => getStationOptions(allStations ?? []), [allStations]);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const canRemove = stations.length > 2;
    const [isDragging, setIsDragging] = useState(false);
    const loadingSegmentsRef = useRef<Set<string>>(new Set());
    const invalidSegmentsRef = useRef<Set<string>>(new Set());
    const showRowErrors = stations.filter((s) => s.code.length > 0).length >= 2;

    const handleLoadingChange = useCallback(
        (key: string, loading: boolean) => {
            const prevHad = loadingSegmentsRef.current.size > 0;
            if (loading) loadingSegmentsRef.current.add(key);
            else loadingSegmentsRef.current.delete(key);
            const nextHas = loadingSegmentsRef.current.size > 0;
            if (prevHad !== nextHas) onHasLoadingRijtijden(nextHas);
        },
        [onHasLoadingRijtijden]
    );

    const handleInvalidChange = useCallback(
        (key: string, invalid: boolean) => {
            const prevHad = invalidSegmentsRef.current.size > 0;
            if (invalid) invalidSegmentsRef.current.add(key);
            else invalidSegmentsRef.current.delete(key);
            const nextHas = invalidSegmentsRef.current.size > 0;
            if (prevHad !== nextHas) onHasInvalidRijtijden(nextHas);
        },
        [onHasInvalidRijtijden]
    );

    const handleDragStart = (_event: DragStartEvent) => {
        setIsDragging(true);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setIsDragging(false);
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = stations.findIndex((s) => s.id === active.id);
        const newIndex = stations.findIndex((s) => s.id === over.id);
        const newStations = [...stations];
        const [removed] = newStations.splice(oldIndex, 1);
        newStations.splice(newIndex, 0, removed);
        onStationsChange(newStations);
    };

    const handleChangeStation = (index: number, code: string) => {
        if (stations[index].code === code) return;
        const newStations = stations.map((s, i) => (i === index ? { ...s, code } : s));
        onStationsChange(newStations);
    };

    const handleRemoveStation = (index: number) => {
        onStationsChange(stations.filter((_, i) => i !== index));
    };

    const handleAddStation = (atIndex: number) => {
        const newStation: EditableStation = { id: crypto.randomUUID(), code: "" };
        const newStations = [...stations];
        newStations.splice(atIndex, 0, newStation);
        onStationsChange(newStations);
    };

    return (
        <div>
            <div className="text-content-emphasis bg-emphasis-secondary mb-2 flex border-b py-2 pl-1 text-xs font-bold">
                <span>Rijtijd</span>
                <span className="text-content-subtle ml-1">(uu:mm)</span>
                <span className="ml-8">Stations</span>
            </div>

            {/* Add station at the top */}
            <div className="flex">
                <div className="w-16 shrink-0" />
                <div className="ml-1 flex flex-1 items-center gap-2 py-1">
                    <div className="border-border-subtle flex-1 border-t" />
                    <Button variant="tertiary" size="s" icon="plus" onClick={() => handleAddStation(0)}>
                        Station toevoegen
                    </Button>
                </div>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext items={stations.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="flex flex-col">
                        {stations.map((station, index) => {
                            const isFirst = index === 0;
                            const isLast = index === stations.length - 1;
                            const prevStation = stations[index - 1];
                            const nextStation = stations[index + 1];

                            const isEmptyError = showRowErrors && station.code.length === 0;
                            const duplicatesPrev = !!prevStation?.code && prevStation.code === station.code;
                            const duplicatesNext = !!nextStation?.code && nextStation.code === station.code;
                            const hasError = isEmptyError || duplicatesPrev || duplicatesNext;

                            const segmentKey = prevStation ? `${prevStation.id}:${station.id}:${prevStation.code}:${station.code}` : "";

                            return (
                                <div key={station.id}>
                                    {/* Segment between previous and current station */}
                                    {index > 0 && prevStation && (
                                        <TimelineSegmentEditor
                                            key={segmentKey}
                                            beginStation={prevStation.code}
                                            eindStation={station.code}
                                            isDuplicate={prevStation.code.length > 0 && prevStation.code === station.code}
                                            rijtijden={rijtijden}
                                            suppressFetch={isDragging}
                                            onRijtijdChange={onRijtijdChange}
                                            onAddStation={() => handleAddStation(index)}
                                            onLoadingChange={(loading) => handleLoadingChange(segmentKey, loading)}
                                            onInvalidChange={(invalid) => handleInvalidChange(segmentKey, invalid)}
                                        />
                                    )}

                                    {/* Station row */}
                                    <div className="flex items-stretch">
                                        <div className="flex w-16 shrink-0 flex-col items-center">
                                            <TimelineLine visible={!isFirst} />
                                            <TimelineStationIndicator variant={isFirst || isLast ? "endpoint" : "intermediate"} />
                                            <TimelineLine visible={!isLast} />
                                        </div>
                                        <div className="ml-1 flex flex-1 items-center">
                                            <div className="flex-1">
                                                <StationRow
                                                    id={station.id}
                                                    stationCode={station.code}
                                                    stationOptions={stationOptions}
                                                    onChangeStation={(code) => handleChangeStation(index, code)}
                                                    onRemove={() => handleRemoveStation(index)}
                                                    canRemove={canRemove}
                                                    hasError={hasError}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </SortableContext>
            </DndContext>

            {/* Always-visible add station at the bottom */}
            <div className="flex">
                <div className="w-16 shrink-0" />
                <div className="ml-1 flex flex-1 justify-end py-1">
                    <Button variant="tertiary" size="s" icon="plus" onClick={() => handleAddStation(stations.length)}>
                        Station toevoegen
                    </Button>
                </div>
            </div>
        </div>
    );
}

type TimelineSegmentEditorProps = {
    beginStation: string;
    eindStation: string;
    isDuplicate: boolean;
    rijtijden: Rijtijd[];
    suppressFetch: boolean;
    onRijtijdChange: (beginStation: string, eindStation: string, reisMinuten: number | undefined) => void;
    onAddStation: () => void;
    onLoadingChange: (loading: boolean) => void;
    onInvalidChange: (invalid: boolean) => void;
};

function TimelineSegmentEditor({
    beginStation,
    eindStation,
    isDuplicate,
    rijtijden,
    suppressFetch,
    onRijtijdChange,
    onAddStation,
    onLoadingChange,
    onInvalidChange,
}: Readonly<TimelineSegmentEditorProps>) {
    const existingRijtijd = rijtijden.find((r) => r.beginStation === beginStation && r.eindStation === eindStation);
    const bothStationsSet = beginStation.length > 0 && eindStation.length > 0;
    const shouldFetch = bothStationsSet && !isDuplicate && existingRijtijd === undefined && !suppressFetch;
    const { data: fetchedRijtijd, isLoading, error } = useGetTvvRijtijd(beginStation, eindStation, { enabled: shouldFetch });
    const isNotFound = error instanceof ApiError && error.status === 404;
    const isOtherError = error != null && !isNotFound;

    const [localValue, setLocalValue] = useState<string>(
        existingRijtijd?.reisMinuten != null ? Rijtijd.formatSegmentRijtijd(existingRijtijd.reisMinuten) : ""
    );
    const [userEdited, setUserEdited] = useState(false);
    const [autoFetched, setAutoFetched] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);

    // Apply fetched rijtijd if user hasn't edited
    useEffect(() => {
        if (fetchedRijtijd?.reisMinuten != null && !userEdited) {
            setLocalValue(Rijtijd.formatSegmentRijtijd(fetchedRijtijd.reisMinuten));
            setAutoFetched(true);
            onRijtijdChange(beginStation, eindStation, fetchedRijtijd.reisMinuten);
        }
    }, [fetchedRijtijd, beginStation, eindStation, onRijtijdChange, userEdited]);

    // Track loading state
    const onLoadingChangeRef = useRef(onLoadingChange);
    onLoadingChangeRef.current = onLoadingChange;
    useEffect(() => {
        onLoadingChangeRef.current(isLoading && shouldFetch);
        return () => onLoadingChangeRef.current(false);
    }, [isLoading, shouldFetch]);

    // Clear invalid state on unmount so stale keys don't keep the Apply button disabled
    const onInvalidChangeRef = useRef(onInvalidChange);
    onInvalidChangeRef.current = onInvalidChange;
    useEffect(() => {
        return () => onInvalidChangeRef.current(false);
    }, []);

    // Read-only only for rijtijden auto-fetched in this session, not for ones passed in via props
    const isKnown = autoFetched && !userEdited;
    const hasAnyRijtijd = existingRijtijd?.reisMinuten != null || autoFetched || userEdited;
    const isOnbekend = bothStationsSet && !isDuplicate && !hasAnyRijtijd && !isLoading && !suppressFetch && !isOtherError;
    const isDisabled = !bothStationsSet || isDuplicate;
    const inputClassName = cn(
        "rounded-m w-14 px-2 py-1 text-center text-xs",
        isDisabled && "bg-accent-gray-subtle cursor-not-allowed",
        !isDisabled && isKnown && "bg-accent-gray-subtle cursor-default",
        !isDisabled && !isKnown && isInvalid && "border-error border",
        !isDisabled && !isKnown && !isInvalid && "border-input-default border"
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
        if (!userEdited) setUserEdited(true);
    };

    const handleBlur = () => {
        if (localValue.trim() === "") {
            setUserEdited(false);
            setIsInvalid(false);
            onInvalidChange(false);
            onRijtijdChange(beginStation, eindStation, undefined);
            return;
        }
        const minutes = Rijtijd.parseSegmentRijtijd(localValue);
        if (minutes == null) {
            setIsInvalid(true);
            onInvalidChange(true);
            return;
        }
        setIsInvalid(false);
        onInvalidChange(false);
        setLocalValue(Rijtijd.formatSegmentRijtijd(minutes));
        onRijtijdChange(beginStation, eindStation, minutes);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleBlur();
    };

    return (
        <div className="flex items-stretch">
            <div className="flex w-16 shrink-0 flex-col items-center">
                <TimelineLine />
                <div className="py-1">
                    <input
                        type="text"
                        placeholder="00:00"
                        value={localValue}
                        readOnly={isKnown}
                        disabled={isDisabled}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        className={inputClassName}
                    />
                </div>
                <TimelineLine />
            </div>

            <div className="ml-1 flex flex-1 items-center gap-2 py-1">
                {isLoading && shouldFetch && (
                    <span className="text-content-subtle flex shrink-0 items-center gap-1 text-xs">
                        <Icon name="loader" size="xs" className="animate-spin" />
                        Rijtijd ophalen..
                    </span>
                )}
                {isInvalid && (
                    <span className="text-content-error flex shrink-0 items-center gap-1 text-xs">
                        <Icon name="error" size="xs" />
                        Vul geldig formaat in of laat het veld leeg
                    </span>
                )}
                {isOtherError && !isInvalid && (
                    <span className="text-content-error flex shrink-0 items-center gap-1 text-xs">
                        <Icon name="error" size="xs" />
                        Fout bij ophalen rijtijd
                    </span>
                )}
                {(isOnbekend || (isNotFound && !hasAnyRijtijd)) && !isInvalid && (
                    <span className="text-content-warning flex shrink-0 items-center gap-1 text-xs">
                        <Icon name="alert" size="xs" />
                        Rijtijd onbekend
                    </span>
                )}
                <div className="border-border-subtle flex-1 border-t" />
                <div className="shrink-0">
                    <Button variant="tertiary" size="s" icon="plus" onClick={onAddStation}>
                        Station toevoegen
                    </Button>
                </div>
            </div>
        </div>
    );
}
